// import { useState } from 'react';
import { awaitingResolutionSubStatuses, paymentVerificationSubStatuses } from './SubStatusConfig';
import NotificationIcons from './NotificationIcons';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFilterStore } from '../../stores/filter-store';

const dashboardStatuses = [
  { 
    label: 'Outstanding', 
    activeIcon: '/img/delivery-time.svg',
    inactiveIcon: '/img/delivery-time.svg',
    hoverIcon: '/img/delivery-time.svg',
    value: 'outstanding'
  },
  { 
    label: 'Payment Verification', 
    activeIcon: '/img/wallet.svg',
    inactiveIcon: '/img/wallet copy.svg',
    hoverIcon: '/img/wallet.svg',
    value: 'payment_verification'
  },
  { 
    label: 'Ready for Procurement', 
    activeIcon: '/img/check-circle.svg',
    inactiveIcon: '/img/check-circle copy.svg',
    hoverIcon: '/img/check-circle.svg',
    value: 'ready_for_procurement'
  },
  { 
    label: 'Fulfilled', 
    activeIcon: '/img/fullfeld.svg',
    inactiveIcon: '/img/fullfeld copy.svg',
    hoverIcon: '/img/fullfeld.svg',
    value: 'fulfilled'
  },
  { 
    label: 'Awaiting Resolution', 
    activeIcon: '/img/waiting.svg',
    inactiveIcon: '/img/waiting copy.svg',
    hoverIcon: '/img/waiting.svg',
    value: 'awaiting_resolution'
  },
];

export default function FulfillmentTabs({ selected, onSelect, onSubStatusSelect }: {
  selected?: string;
  onSelect?: (status: string) => void;
  onSubStatusSelect?: (subStatus: string) => void;
}) {
  const { 
    fulfillmentFilters, 
    setFulfillmentStatus, 
    setFulfillmentSubStatus 
  } = useFilterStore();
  
  // Use the selected prop directly instead of internal state
  const activeStatus = selected || fulfillmentFilters.status;

  // Try to use language context
  let t;
  try {
    const { t: translate } = useLanguage();
    t = translate;
  } catch (error) {
    t = (key: string) => key; // Fallback
  }

  const handleStatusClick = (statusValue: string) => {
    setFulfillmentStatus(statusValue);
    onSelect?.(statusValue);
  };

  const handleSubStatusClick = (subStatusValue: string) => {
    setFulfillmentSubStatus(subStatusValue);
    onSubStatusSelect?.(subStatusValue);
  };

  const handleAwaitingSubStatusClick = (subStatusValue: string) => {
    setFulfillmentSubStatus(subStatusValue);
    onSubStatusSelect?.(subStatusValue);
  };

  // Function to get translated label
  const getTranslatedLabel = (label: string) => {
    const labelMap: { [key: string]: string } = {
      'Outstanding': t('dashboard.outstanding'),
      'Payment Verification': t('dashboard.paymentVerification'),
      'Ready for Procurement': t('dashboard.readyForProcurement'),
      'Fulfilled': t('dashboard.fulfilled'),
      'Awaiting Resolution': t('dashboard.awaitingResolution'),
      'Payment Verified': t('dashboard.paymentVerified'),
      'Pending Item Completion': t('dashboard.pendingItemCompletion'),
      'Item Completion unverified': t('dashboard.itemCompletionUnverified'),
      'Canceled': t('dashboard.canceled'),
      'On Hold': t('dashboard.onHold'),
      'Partial Fulfilment': t('dashboard.partialFulfilment')
    };
    return labelMap[label] || label;
  };

  return (
    <div className="space-y-4">
      {/* Main Fulfillment Tabs */}
      <div className="flex justify-between items-center mb-6 mt-[-24px] overflow-x-auto py-3 bg-gray-200 px-8 outline outline-1 outline-gray-200 relative z-10">
        <div className="flex gap-3">
          {dashboardStatuses.map((status) => {
            const isSelected = activeStatus === status.value;
            const translatedLabel = getTranslatedLabel(status.label);
            return (
              <button
              key={status.value}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer
                ${isSelected
                  ? 'text-primary-500'
                  : 'text-gray-600 hover:text-primary-500/40'}
              `}
              onClick={() => handleStatusClick(status.value)}
            >
              <div className={`w-11 h-11 rounded-lg flex items-center justify-center transition-colors ${
                isSelected ? 'bg-primary-500' : 'bg-white hover:bg-gray-50'
              }`}>
                <img
                  src={status.activeIcon}
                  alt={translatedLabel}
                  className="w-6 h-6"
                />
              </div>
              <span>
                {status.label === 'Payment Verification' ? (
                  <>{getTranslatedLabel('Payment Verification').includes(' ') ? 
                    getTranslatedLabel('Payment Verification').split(' ').map((word, index, array) => 
                      index === array.length - 1 ? <span key={index}>{word}</span> : <span key={index}>{word}<br /></span>
                    ) : 
                    getTranslatedLabel('Payment Verification')
                  }</>
                ) : status.label === 'Ready for Procurement' ? (
                  <>{getTranslatedLabel('Ready for Procurement').includes(' ') ? 
                    getTranslatedLabel('Ready for Procurement').split(' ').map((word, index) => 
                      index === 0 || index === 1 ? <span key={index}>{word} </span> : <span key={index}><br />{word}</span>
                    ) : 
                    getTranslatedLabel('Ready for Procurement')
                  }</>
                ) : status.label === 'Awaiting Resolution' ? (
                  <>{getTranslatedLabel('Awaiting Resolution').includes(' ') ? 
                    getTranslatedLabel('Awaiting Resolution').split(' ').map((word, index, array) => 
                      index === array.length - 1 ? <span key={index}>{word}</span> : <span key={index}>{word}<br /></span>
                    ) : 
                    getTranslatedLabel('Awaiting Resolution')
                  }</>
                ) : (
                  translatedLabel
                )}
              </span>
            </button>
            );
          })}
        </div>

        {/* Notification Icons */}
        <NotificationIcons />
      </div>

      {/* Sub-Status Filters - Only show when Payment Verification is selected */}
      {activeStatus === 'payment_verification' && (
        <div className="relative z-0 mt-4 px-8">
          <div className="relative z-0 ml-[100px] mt-[-30px] mb-8 flex w-fit">
            {paymentVerificationSubStatuses.map((subStatus, index) => {
              const isSelected = fulfillmentFilters.subStatus === subStatus.value;
              const overlapClass = index === 0 ? '' : '-ml-6';
              const translatedLabel = getTranslatedLabel(subStatus.label);
              
              return (
                <button
                  key={subStatus.value}
                  className={`${overlapClass} px-8 py-2.5 first:pl-7 last:pr-7 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center rounded-full border relative
                    ${isSelected 
                      ? 'bg-[#FFF8D6] text-[#D4A017] font-semibold border-[#F2BC00]' 
                      : 'bg-white text-[#1E3255] border-[#4B5675] hover:bg-[#F7F9FC]'
                    }
                  `}
                  style={{ zIndex: 20 - index }}
                  onClick={() => handleSubStatusClick(subStatus.value)}
                >
                  {translatedLabel}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Sub-Status Filters - Only show when Awaiting Resolution is selected */}
      {activeStatus === 'awaiting_resolution' && (
        <div className="relative z-0 mt-4 px-8">
          <div className="relative z-0 ml-[470px] mt-[-30px] mb-8 flex w-fit">
            {awaitingResolutionSubStatuses.map((subStatus, index) => {
              const isSelected = fulfillmentFilters.subStatus === subStatus.value;
              const overlapClass = index === 0 ? '' : '-ml-6';
              const translatedLabel = getTranslatedLabel(subStatus.label);
              
              return (
                <button
                  key={subStatus.value}
                  className={`${overlapClass} px-8 py-2.5 first:pl-7 last:pr-7 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center rounded-full border relative
                    ${isSelected 
                      ? 'bg-[#FFF8D6] text-[#D4A017] font-semibold border-[#F2BC00]' 
                      : 'bg-white text-[#1E3255] border-[#4B5675] hover:bg-[#F7F9FC]'
                    }
                  `}
                  style={{ zIndex: 20 - index }}
                  onClick={() => handleAwaitingSubStatusClick(subStatus.value)}
                >
                  {translatedLabel}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}